
import { Component, DestroyRef, inject, OnInit, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { CatalogSku } from 'src/app/common/CatalogSku';
import { CartService } from 'src/app/services/cart.service';
import { AppProfileService } from '../../services/profile.service';
import { ExtendedProfile } from '../../common/ExtendedProfile';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { apiErrors, parseApiProblem } from '../../common/ApiError';
import { errorCodeText } from '../../common/error-messages';
import { isOrderValidationReason, orderValidationReasonText } from '../../common/OrderValidation';

@Component({
    imports: [FormsModule, AsyncClickDirective],
    selector: 'app-add-to-cart',
    templateUrl: './add-to-cart.component.html',
    styleUrls: ['./add-to-cart.component.css']
})
export class AddToCartComponent implements OnInit {
  private cartService = inject(CartService);
  private toastr = inject(ToastrService);
  private profileService = inject(AppProfileService);

  private destroyRef = inject(DestroyRef);
  readonly catalogSku = input<CatalogSku>();
  public count: number;

  public profile: ExtendedProfile = null;

  ngOnInit(): void {
    this.count = 1;
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
    });
    if (!this.cartService.cart) {
      this.cartService.reloadCart();
    }
  }

  // Returns the (cold) Observable so [asyncClick] owns the subscription and guards
  // against a double-click while the request is in flight. See AsyncClickDirective.
  // Hand-rolled toast pipe (instead of callWithToasts$) because the error message is
  // error-dependent: backend #525 rejects a currency mix with a 409 that deserves its own
  // explanation, not the generic failure toast. Same contract: success toast on next,
  // error toast + rethrow so AsyncClickDirective still sees the error.
  public addToCart(): Observable<boolean> {
    return this.cartService.addToCart(this.catalogSku(), this.count).pipe(
      tap(() => this.toastr.success($localize`Der Artikel wurde zum Warenkorb hinzugefügt.`)),
      catchError((err) => {
        this.toastr.error(this.addToCartErrorText(err));
        return throwError(() => err);
      })
    );
  }

  // Backend #525 (D1): a cart is single-currency — the mixed-cart 409 carries
  // { cartCurrency, skuCurrency, skuId }. The BFF forwards the body verbatim, so it may
  // arrive as the parsed object or a JSON string.
  private addToCartErrorText(err: any): string {
    const body = parseApiProblem<{ cartCurrency?: string; skuCurrency?: string }>(err);
    if (err?.status === 409 && body?.cartCurrency && body?.skuCurrency) {
      return $localize`Ihr Warenkorb enthält Artikel in ${body.cartCurrency}. Dieser Artikel ist in ${body.skuCurrency} ausgezeichnet — ein Warenkorb kann nur eine Währung enthalten. Bitte schließen Sie den aktuellen Einkauf zuerst ab.`;
    }

    // #178 / backend #682: the 422 is problem details now, and its one error uses the checkout
    // gate's own vocabulary (`notFound` / `imported` / `vatUndetermined`) plus `ownListing`.
    // Until this, every one of those read „Bei der Aktualisierung des Warenkorbs ist ein Fehler
    // aufgetreten" — a sentence that tells a buyer nothing about a listing that is permanently
    // unorderable. The gate's wording lives in OrderValidation.ts rather than the code
    // dictionary, because it is written per position and carries quantities and currencies.
    const coded = apiErrors(err)[0];
    if (coded?.code) {
      if (isOrderValidationReason(coded.code)) {
        return orderValidationReasonText({ reason: coded.code });
      }
      const text = errorCodeText(coded);
      if (text) {
        return text;
      }
    }

    return $localize`Bei der Aktualisierung des Warenkorbs ist ein Fehler aufgetreten`;
  }

  public CanAddToCart(): boolean {
    const catalogSku = this.catalogSku();
    return (
      this.profile?.customer?.id &&
      catalogSku?.skuId &&
      this.profile.customer.id !== catalogSku.sellerCustomerId &&
      // #509: only platform-stocked listings are orderable; 'availableAtSource' /
      // 'unavailable' rows are index-only (the list hides this component for them —
      // this guard is defense-in-depth for other embeddings). Backend #562 (decision 3)
      // makes availability the field that suppresses the order action, so an absent or
      // unrecognised value counts as NOT orderable: offering an order we know the
      // checkout gate will reject is worse than hiding one that would have worked.
      catalogSku.availability === 'available'
    );
  }
}
